import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import useTranslate from '@src/i18n/useTranslate';
import { LayoutColors } from '@src/core/theme';
import {
  BodyExtraSmall,
  BodyLarge,
  BodyLargeBold,
  BodyRegular,
  BodyRegularMedium,
  BodyRegularSemiBold,
  BodySmall,
  BodySmallMedium,
  Heading6,
} from '@src/fonts/typography/Typography';
import { device } from '@src/common/breakpoints';

import DotFalling from '@src/components/reusable/spinner/DotFalling';
import { PaymentMethodFormProps } from '@src/features/payments/types';
import { track } from '@src/services/analytics';
import { useAppSelector } from '@src/core/hooks';
import { paymentOngoingSelector } from '@src/features/payments/store/payment';
import Alert, { AlertTitle } from '@src/components/reusable/Alert';
import { PaymentErrorsEnum, checkInsufficientAmountError } from '@src/domains/payments';
import { resolveTransactableEntity } from '@src/domains/transactables';
import { PIX_POLLING_INTERVAL, usePixPayment } from '@src/features/payments/pix';

export const PixPaymentForm = (props: PaymentMethodFormProps) => {
  const { order, sale, directLink, saleChange, paymentOption, handleSubmit, saleStatusRequester, errors } = props;
  const { i18n, i18nElement } = useTranslate();

  const paymentOngoing = useAppSelector(paymentOngoingSelector);
  const paymentEntity = resolveTransactableEntity({ order, sale, directLink, saleChange });
  const lastTransaction = paymentEntity?.lastTransaction;

  const { isPending: lastAttemptIsPendingPix, isExpired } = usePixPayment({
    transaction: lastTransaction,
    pollingIntervalMs: PIX_POLLING_INTERVAL,
    onSync: saleStatusRequester,
  });

  const [showTooltip, setShowTooltip] = useState(false);
  const codeRef = useRef<HTMLTextAreaElement>(null);

  const hasInsufficientAmountError = useMemo(() => checkInsufficientAmountError(errors), [errors]);

  useEffect(() => {
    track('checkoutPaymentFormPix');
  }, []);

  if (!lastAttemptIsPendingPix || isExpired) {
    let errorMessage: string | null = null;

    if (errors && PaymentErrorsEnum.TRANSACTION_ERROR in errors && errors[PaymentErrorsEnum.TRANSACTION_ERROR]) {
      errorMessage = errors[PaymentErrorsEnum.TRANSACTION_ERROR].join(', ');
    } else if (isExpired) {
      errorMessage = i18n('checkout_expiredTry');
    }

    return (
      <PixPayment onSubmit={handleSubmit}>
        <PixTitle>PIX</PixTitle>
        <PixDescription>{i18nElement('checkout_pix_proceed_description')}</PixDescription>

        {hasInsufficientAmountError ? (
          <Alert severity='error' variant='ghost' sx={{ padding: '8px', margin: '16px 0px 0px' }}>
            <AlertContentWrapper>
              <StyledAlertTitle>{i18n('checkout_pix_payment_insufficient_amount_title')}</StyledAlertTitle>
              {i18n('checkout_pix_payment_insufficient_amount_description')}
            </AlertContentWrapper>
          </Alert>
        ) : null}

        <PixProceedButton>
          {paymentOngoing ? <DotFalling color={LayoutColors.whiteLight} /> : i18nElement('checkout_continue_pix')}
        </PixProceedButton>
        <input type='hidden' name='paymentMethodId' value={paymentOption?.paymentMethod.id} />
        <TextError hasError={Boolean(errorMessage?.length)}>{errorMessage}</TextError>
      </PixPayment>
    );
  }

  const handleCodeToClipboard = () => {
    if (codeRef.current) {
      codeRef.current.select();
      document.execCommand('copy'); // In the future must replace this approach by the navigator.clipboard one, but only this works on safari for now

      setShowTooltip(() => {
        setTimeout(() => {
          setShowTooltip(false);
        }, 3000);
        return true;
      });
    }
  };

  return (
    <>
      <FinishPaymentPix>
        <FinishPaymentPixTitle>PIX</FinishPaymentPixTitle>
        <PaymentStatus>
          <FinishPaymentTitle>{i18nElement('checkout_finishPaymentWithPix')}</FinishPaymentTitle>
          <FinishPaymentDesc>{i18nElement('checkout_pixUseCameraDescription')}</FinishPaymentDesc>
          <StatusBox>
            <Loading />
            <PaymentVerification>{i18n('checkout_verifyingPayment')}</PaymentVerification>
          </StatusBox>
        </PaymentStatus>

        {lastTransaction?.pix ? (
          <PixPaymentInfoCodes>
            <PixPaymentQRCode>
              <img alt={i18n('checkout_qrCodeWithPixCode')} src={lastTransaction.pix.qrCode} />
            </PixPaymentQRCode>
            <PixPaymentCodeText>
              <TextCode ref={codeRef} readOnly={true} value={lastTransaction.pix.code} />
              <CopyTextCode onClick={handleCodeToClipboard}>
                <CopyButton>{i18nElement('checkout_copyCode')}</CopyButton>
                {showTooltip ? (
                  <Tooltip>
                    <p>{i18nElement('checkout_copiedCode')}</p>
                  </Tooltip>
                ) : null}
              </CopyTextCode>
            </PixPaymentCodeText>
          </PixPaymentInfoCodes>
        ) : null}

        <PixCompletePurchaseTitle>{i18nElement('checkout_completePurchaseStepsTitle')}</PixCompletePurchaseTitle>
        <PurchaseSteps>
          <Step>{i18nElement('checkout_ongoingPixDescription')}</Step>
          {lastTransaction?.pix ? (
            <Step>
              <span>{i18nElement('checkout_confirm')}</span>
              <strong>{` ${lastTransaction.pix.receiverInfo.financialInstitution}`}.</strong>
              <span>; {i18nElement('checkout_nameOfReceiver')} </span>
              <strong>{lastTransaction.pix.receiverInfo.companyName}.</strong>
              <span>; CNPJ: </span>
              <strong>{lastTransaction.pix.receiverInfo.cnpj}</strong>
              <span>).</span>
            </Step>
          ) : null}
        </PurchaseSteps>

        <PurchaseWarnings>
          <p>{i18nElement('checkout_atention')}</p>
          <ul>
            <li>{i18nElement('checkout_warning1')}</li>
            <li>{i18nElement('checkout_warning2')}</li>
            <li>{i18nElement('checkout_warning3')}</li>
          </ul>
        </PurchaseWarnings>
      </FinishPaymentPix>
    </>
  );
};

interface TextErrorProps {
  hasError: boolean;
}

const PixPayment = styled.form`
  width: 100%;
`;

const PixTitle = styled.h4`
  ${BodyLargeBold};
  color: ${LayoutColors.justBlue};
  margin-bottom: 16px;
`;

const PixDescription = styled.p`
  ${BodySmallMedium};

  ${device.mediumTablet} {
    ${BodyRegularMedium};
  }
`;

const TextError = styled.p<TextErrorProps>`
  ${(props) => (props.hasError ? '' : 'display: none')};
  ${BodyExtraSmall};
  margin-top: 8px;
  color: ${LayoutColors.errorDark};
`;

const PixProceedButton = styled.button`
  width: 100%;
  background-color: ${LayoutColors.successDark};
  height: 44px;
  ${BodyRegularSemiBold};
  border-radius: 4px;
  padding: 8px;
  margin-top: 16px;
  border: none;
  cursor: pointer;
  color: ${LayoutColors.whiteLight};
`;

const FinishPaymentPix = styled.div`
  width: 100%;
`;

const FinishPaymentPixTitle = styled.h2`
  ${Heading6};
  color: ${LayoutColors.justBlue};
  margin-bottom: 16px;
`;

const PaymentStatus = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  text-align: center;

  margin-bottom: 4px;

  ${device.mediumTablet} {
    margin-bottom: 30px;
  }
`;

const FinishPaymentTitle = styled.p`
  ${BodyRegularSemiBold};
  margin-bottom: 8px;
`;

const FinishPaymentDesc = styled.p`
  ${BodyRegular};
  margin-bottom: 24px;
`;

const StatusBox = styled.div`
  width: 100%;
  height: 76px;
  border-radius: 8px;
  background-color: rgba(32, 151, 247, 0.1);
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: ${LayoutColors.justBlue};

  ${device.mobile} {
    gap: 12px;
    padding: 12px;
  }

  ${device.smallTablet} {
    width: 324px;
    gap: 16px;
    padding: 16px;
  }
`;

const Loading = styled.div`
  width: 16px;
  height: 10px;
  border-radius: 100%;
  border: 1px solid ${LayoutColors.justBlue};
  border-bottom-color: rgba(0, 0, 0, 0);
  animation: Rotate 0.5s linear infinite;
  @keyframes Rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @media (min-width: 340px) {
    width: 16px;
    height: 16px;
  }

  ${device.mobile} {
    gap: 12px;
    width: 22px;
    height: 22px;
    border: 3px solid ${LayoutColors.justBlue};
    border-bottom-color: rgba(0, 0, 0, 0);
  }

  ${device.smallTablet} {
    width: 44px;
    height: 44px;
    border: 5px solid ${LayoutColors.justBlue};
    border-bottom-color: rgba(0, 0, 0, 0);
  }
`;

const PaymentVerification = styled.p`
  color: ${LayoutColors.justBlue};
  ${device.mobile} {
    ${BodyLarge};
    color: ${LayoutColors.justBlue};
  }

  margin-left: 16px;
  ${BodySmallMedium};
  color: ${LayoutColors.justBlue} !important;
`;

const PixPaymentInfoCodes = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  gap: 8px;

  ${device.smallTablet} {
    flex-flow: row;
    align-items: flex-end;
  }
`;

const PixPaymentQRCode = styled.div`
  width: 100%;

  img {
    width: 100%;
  }

  ${device.mobile} {
    width: 150px;
    height: 150px;
  }
`;

const PixPaymentCodeText = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;
  ${device.smallTablet} {
    width: calc(100% - 142px);
  }
`;

const TextCode = styled.textarea`
  width: 100%;
  height: 90px;
  ${BodyRegular};
  padding: 8px;
  border: 1px solid #cdcdcd;
  border-radius: 4px;
  resize: none;

  &::-webkit-scrollbar-thumb {
    background: ${LayoutColors.blackLight};
    border-radius: 10px;
  }
  &::-webkit-scrollbar {
    width: 9px;
  }
`;

const CopyTextCode = styled.div`
  margin-top: 8px;
  width: 100%;
  position: relative;
  border-style: none;
  border: none;
  cursor: pointer;
`;

const CopyButton = styled.button`
  width: 100%;
  border-radius: 4px;
  height: 44px;
  background-color: ${LayoutColors.successDark};
  ${BodyRegularSemiBold};
  color: ${LayoutColors.whiteLight};
  border: none;
  border-style: none;
`;

const Tooltip = styled.div`
  width: 106px;
  height: 31px;
  top: -31px;
  position: absolute;
  left: calc(50% - 53px);
  ${BodyExtraSmall};
  border-radius: 8px;
  background-color: ${LayoutColors.blackMain};
  display: flex;
  align-items: center;
  p {
    margin: auto;
    color: ${LayoutColors.whiteLight};
  }
`;

const PixCompletePurchaseTitle = styled.div`
  text-align: center;
  ${BodyRegularSemiBold};
  margin: 32px 0 8px 0;

  ${device.mediumTablet} {
    margin: 42px 0 8px 0;
  }
`;

const PurchaseSteps = styled.ol`
  position: relative;
  margin-top: 8px;
  list-style-position: inside;
`;

const Step = styled.li`
  position: relative;
  ${BodySmall};
  margin-top: 8px;
`;

const PurchaseWarnings = styled.div`
  ${BodySmall};
  margin-top: 16px;

  ul {
    list-style-position: inside;
    list-style: none;
    li:not(:first-child) {
      margin-top: 20px;
    }
  }
`;

const StyledAlertTitle = styled(AlertTitle)`
  ${BodySmallMedium};
  font-weight: 600;
  color: ${LayoutColors.errorDark};
`;

const AlertContentWrapper = styled.section`
  display: flex;
  gap: 8px;
  align-self: stretch;
  flex-direction: column;
  align-items: flex-start;

  ${BodySmall};
  color: ${LayoutColors.errorDark} !important;
`;
